class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("keyup", (event) => {
      if (event.code === "Escape" && this.classList.contains("active"))
        this.close();
    });
    this.addEventListener("click", (event) => {
      if (event.target.id === "CartDrawer-Overlay") this.close();
    });
  }

  connectedCallback() {
    this.headerEvents = new AbortController();
    const options = { signal: this.headerEvents.signal };
    this.setHeaderCartIconAccessibility();
    document.addEventListener(
      "click",
      (event) => {
        const trigger = event.target.closest("#cart-icon-bubble");
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      },
      options,
    );
    document.addEventListener(
      "keydown",
      (event) => {
        const trigger = event.target.closest("#cart-icon-bubble");
        if (!trigger || event.code !== "Space") return;
        event.preventDefault();
        this.open(trigger);
      },
      options,
    );
    document.addEventListener(
      "shopify:section:load",
      () => this.setHeaderCartIconAccessibility(),
      options,
    );
  }

  disconnectedCallback() {
    this.headerEvents?.abort();
    if (this.classList.contains("active")) this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains("active")) return;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add("animate", "active");
    document.body.classList.add("overflow-hidden");
    this.trapDrawerFocus();
    this.querySelector("cart-drawer-items")?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove("active");
    const opener = this.activeElement?.isConnected
      ? this.activeElement
      : document.querySelector("#cart-icon-bubble");
    removeTrapFocus(opener);
    document.body.classList.remove("overflow-hidden");
  }

  trapDrawerFocus() {
    if (!this.classList.contains("active")) return;
    const container = this.querySelector(".drawer__inner");
    const focusElement =
      container?.querySelector(".drawer__close") || container;
    if (container && focusElement) trapFocus(container, focusElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute(
      "aria-expanded",
      String(cartDrawerNote.closest("details").open),
    );
    const contentId = cartDrawerNote.nextElementSibling?.id;
    if (contentId) cartDrawerNote.setAttribute("aria-controls", contentId);
    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        String(!event.currentTarget.closest("details").open),
      );
    });
    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState, openDrawer = true) {
    const replacements = this.getSectionsToRender().map((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const source = this.getSectionDOM(
        parsedState.sections?.[section.id],
        section.selector,
      );
      if (!target || !source)
        throw new Error(
          window.cartStrings?.error || "Unable to update cart sections.",
        );
      return { target, source };
    });
    replacements.forEach(({ target, source }) => {
      target.innerHTML = source.innerHTML;
    });
    this.productId = parsedState.id;
    this.classList.toggle(
      "is-empty",
      Boolean(this.querySelector(".drawer__inner-empty")),
    );
    if (this.classList.contains("active")) this.trapDrawerFocus();
    else if (openDrawer) this.open();
  }

  async refreshFromServer({ open = false } = {}) {
    const base = window.routes?.cart_url || "/cart";
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(",");
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${base}?sections=${sectionIds}`),
      fetch(`${base}.js`, { headers: { Accept: "application/json" } }),
    ]);
    if (!sectionResponse.ok || !cartResponse.ok)
      throw new Error(window.cartStrings?.error || "Unable to refresh cart.");
    const [sections, cart] = await Promise.all([
      sectionResponse.json(),
      cartResponse.json(),
    ]);
    this.renderContents({ ...cart, sections }, open);
    return cart;
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const source = this.getSectionDOM(html, selector);
    if (!source)
      throw new Error(
        window.cartStrings?.error || "Unable to update cart sections.",
      );
    return source.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    if (typeof html !== "string" || !html.trim()) return null;
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}
customElements.define("cart-drawer-items", CartDrawerItems);
